import axios, { AxiosInstance, AxiosError } from 'axios';
import { FronteggContext } from '@frontegg/rest-api';

function attachInterceptorsWithFronteggToken(axiosInstance: AxiosInstance) {
  axiosInstance.interceptors.request.use((config) => {
    const token = FronteggContext.getAccessToken();

    const headers: Record<string, string> = {
      Accept: 'application/json',
      Authorization: `Bearer ${token || null}`,
      'Content-Type': 'application/json',
    };

    Object.entries(headers).forEach(([key, value]) => {
      if (!config.headers[key]) {
        config.headers[key] = value;
      }
    });

    return config;
  });

  axiosInstance.defaults.headers.common = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
}

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
});

export const ambientApi = axios.create({
  baseURL: import.meta.env.VITE_AMBIENT_API_BASE_URL,
});

export const adminApi = axios.create({
  baseURL: import.meta.env.VITE_ADMIN_API_BASE_URL,
});

export const integrationsApi = axios.create({
  baseURL: import.meta.env.VITE_INTEGRATIONS_API_BASE_URL,
});

export const aivaApi = axios.create({
  baseURL: import.meta.env.VITE_PHONE_TRIAGE_API_BASE_URL,
});

// All consumers of `magicFlowApi` live in src/modules/SuperAdmin and are
// authenticated as superadmin. The magicflow backend dual-mounts every
// blueprint under `/superadmin` (superadmin role + assume-org); the flat
// routes are reserved for tenant-scoped feature-key auth. Pin the admin
// client to the `/superadmin` prefix so superadmin-only routes are hit.
const magicFlowBaseUrl = (
  import.meta.env.VITE_MAGIC_FLOW_API_BASE_URL || ''
).replace(/\/+$/, '');
export const magicFlowApi = axios.create({
  baseURL: `${magicFlowBaseUrl}/superadmin`,
});

// Customer-scoped magicflow client — hits the flat (tenant-scoped) routes
// rather than the `/superadmin` mount. tasks-v2 (src/pages/tasksV2) is the
// first consumer; staff-facing screens authenticate as a normal practitioner
// and rely on tenant feature-key auth.
export const magicFlowCustomerApi = axios.create({
  baseURL: magicFlowBaseUrl,
});

export const fronteggApi = axios.create({
  baseURL: `${import.meta.env.VITE_FRONTEGG_BASE_URL}/frontegg`,
});

[
  api,
  ambientApi,
  adminApi,
  integrationsApi,
  aivaApi,
  magicFlowApi,
  magicFlowCustomerApi,
  fronteggApi,
].forEach(attachInterceptorsWithFronteggToken);

export const getApiErrorMessage = (
  error: AxiosError<{ message?: string; error?: string }>
): string | undefined => {
  const messageObject = error.response?.data;
  return messageObject?.message || messageObject?.error;
};
